// DOM helpers, toast, modal.
import { iconSvg } from './icons.js';
import { Inbox, Search, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide';
// Strip diacritics so "confitería" matches "confiteria".
export const fold = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const h = (tag, props = {}, ...children) => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props || {})) {
    if (k === 'class' || k === 'className') el.className = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k === 'dataset' && typeof v === 'object') Object.assign(el.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'html') el.innerHTML = v;
    else if (v === true) el.setAttribute(k, '');
    else if (v === false || v == null) {/* skip */}
    else el.setAttribute(k, v);
  }
  for (const c of children.flat(Infinity)) {
    if (c == null || c === false) continue;
    if (typeof c === 'string' || typeof c === 'number') el.appendChild(document.createTextNode(String(c)));
    else el.appendChild(c);
  }
  return el;
};

export const clear = (el) => { while (el.firstChild) el.removeChild(el.firstChild); };

export const showLoading = (el, msg = 'Cargando…') => {
  clear(el);
  el.appendChild(h('div', { class: 'spinner-wrap' },
    h('div', { class: 'spinner' }),
    h('span', { class: 'muted' }, msg),
  ));
};

export const emptyState = (message, { icon = Inbox, title } = {}) =>
  h('div', { class: 'empty-state' },
    h('div', { class: 'empty-state-icon' },
      h('span', { html: iconSvg(icon, 28) }),
    ),
    title ? h('div', { class: 'empty-state-title' }, title) : null,
    h('div', { class: 'empty-state-desc' }, message),
  );

const TOAST_ICONS = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

export const toast = (message, type = 'info') => {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const icon = TOAST_ICONS[type] || Info;
  const el = h('div', { class: `toast ${type}` },
    h('span', { class: 'toast-icon', html: iconSvg(icon, 16) }),
    h('span', { class: 'toast-msg' }, message),
  );
  container.appendChild(el);
  setTimeout(() => {
    el.classList.add('toast-hide');
    setTimeout(() => el.remove(), 250);
  }, 5000);
};

export const showModal = (content, { onClose } = {}) => {
  const backdrop = h('div', { class: 'modal-backdrop' });
  const close = () => {
    document.removeEventListener('keydown', handleKey);
    document.body.removeChild(backdrop);
    if (onClose) onClose();
  };
  const handleKey = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', handleKey);
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) close(); });
  const modal = h('div', { class: 'modal' });
  modal.appendChild(content);
  const closeBtn = h('button', { class: 'modal-close', 'aria-label': 'Cerrar', type: 'button' }, '✕');
  closeBtn.addEventListener('click', close);
  modal.appendChild(closeBtn);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  setTimeout(() => {
    const first = modal.querySelector('input, select, textarea, button');
    if (first) first.focus();
  }, 150);
  return { close, modal };
};

export const promptModal = ({ title, label, placeholder = '', required = false, confirmText = 'Aceptar', cancelText = 'Cancelar', variant = 'primary' } = {}) => {
  return new Promise((resolve) => {
    const content = h('div', {},
      title ? h('h3', {}, title) : null,
      label ? h('label', {}, label) : null,
      h('textarea', { name: 'value', placeholder, rows: 3 }),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-ghost', id: 'prompt-cancel' }, cancelText),
        h('button', { class: `btn ${variant === 'danger' ? 'btn-danger' : variant === 'success' ? 'btn-success' : 'btn'}`, id: 'prompt-ok' }, confirmText),
      ),
    );
    const { close } = showModal(content, { onClose: () => resolve(null) });
    content.querySelector('#prompt-ok').onclick = () => {
      const val = content.querySelector('[name="value"]').value.trim();
      if (required && !val) { toast('Este campo es obligatorio', 'error'); return; }
      resolve(val || null);
      close();
    };
    content.querySelector('#prompt-cancel').onclick = () => { resolve(null); close(); };
  });
};

export const confirmModal = (message, { confirmText = 'Confirmar', cancelText = 'Cancelar', variant = 'danger' } = {}) => {
  return new Promise((resolve) => {
    const content = h('div', {},
      h('p', {}, message),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-ghost', id: 'confirm-cancel' }, cancelText),
        h('button', { class: `btn ${variant === 'danger' ? 'btn-danger' : 'btn-success'}`, id: 'confirm-ok' }, confirmText),
      ),
    );
    const { close } = showModal(content, { onClose: () => resolve(false) });
    content.querySelector('#confirm-ok').onclick = () => { resolve(true); close(); };
    content.querySelector('#confirm-cancel').onclick = () => { resolve(false); close(); };
  });
};

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

export const formatDateOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

// Convert a local datetime-local string to ISO without timezone shift surprises.
export const toIsoLocal = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const fromIsoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  dates_proposed: 'Fechas propuestas',
  confirmed: 'Confirmado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  rejected: 'Rechazado',
  approved: 'Aprobado',
  active: 'Activo',
  inactive: 'Inactivo',
  public: 'Público',
};

export const statusBadge = (status) => h('span', { class: `badge ${status}` }, STATUS_LABELS[status] || status.replace('_', ' '));
